import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { 
  Store, Users, DollarSign, TrendingUp, Package, UserCheck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

export default function AdminDashboard() {
  const { api } = useAuth();
  const { formatPrice } = useCurrency();
  const [stats, setStats] = useState({
    restaurants: 0,
    users: 0,
    orders: 0,
    revenue: 0,
    commission: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [restaurants, users, reports] = await Promise.all([
        api.get('/restaurants'),
        api.get('/users'),
        api.get('/reports/financial'),
      ]);

      setStats({
        restaurants: restaurants.data.length,
        users: users.data.length,
        orders: reports.data.total_orders,
        revenue: reports.data.total_revenue,
        commission: reports.data.total_commission,
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Restaurants', value: stats.restaurants, icon: Store, color: 'text-blue-500', bg: 'bg-blue-50' },
    { title: 'Total Users', value: stats.users, icon: Users, color: 'text-green-500', bg: 'bg-green-50' },
    { title: 'Total Orders', value: stats.orders, icon: Package, color: 'text-purple-500', bg: 'bg-purple-50' },
    { title: 'Total Revenue', value: formatPrice(stats.revenue), icon: DollarSign, color: 'text-orange-500', bg: 'bg-orange-50' },
    { title: 'Total Commission', value: formatPrice(stats.commission), icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="dashboard-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">{stat.title}</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">
                      {loading ? '-' : stat.value}
                    </p>
                  </div>
                  <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-500 text-center py-8">Activity feed coming soon</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <a href="/admin/restaurants" className="block p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <p className="font-medium text-slate-900">Manage Restaurants</p>
              <p className="text-sm text-slate-500">View and manage all restaurants</p>
            </a>
            <a href="/admin/users" className="block p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <p className="font-medium text-slate-900">Manage Users</p>
              <p className="text-sm text-slate-500">View and manage user accounts</p>
            </a>
            <a href="/admin/reports" className="block p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <p className="font-medium text-slate-900">Financial Reports</p>
              <p className="text-sm text-slate-500">View revenue and commission reports</p>
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
